import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  Text,
  Button,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  HStack,
  Badge,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Checkbox,
  useDisclosure,
  useToast,
  Input,
} from "@chakra-ui/react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import SendEmail from "../../components/SendEmail";

const SettlementManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7).replace("-", "")
  );
  const [totalSum, setTotalSum] = useState(0);
  const [commission, setCommission] = useState([]);
  const [totalCommissionSum, setTotalCommissionSum] = useState(0);
  const [userCommission, setUserCommission] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [musicList, setMusicList] = useState([]);
  const [hasContract, setHasContract] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    channel: "",
    agree: false,
  });
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const isFormValid =
    form.name && form.email && form.phone && form.channel && form.agree;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 15;
  const [tempPage, setTempPage] = useState(currentPage.toString());

  // 최근 24개월의 옵션 생성
  const getMonthOptions = () => {
    const options = [];
    const today = new Date();
    // 오늘이 5월이면 5월부터 시작
    for (let i = 0; i < 24; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const value = `${year}${month}`;
      options.push({ value, label: value });
    }
    return options;
  };

  // 정산 데이터 가져오기
  useEffect(() => {
    if (!user?.email) return;
    const fetchSettlements = async (isRetry = false) => {
      setLoading(true);
      setError(null);
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("timeout")), 15000)
        );
        const fetchPromise = supabase.rpc(
          "get_settlement_with_commission_summary",
          {
            target_email: user.email,
            target_month: selectedMonth,
            page_number: currentPage,
          }
        );
        const { data, error } = await Promise.race([
          fetchPromise,
          timeoutPromise,
        ]);
        if (error) {
          if (
            error.code === "57014" ||
            error.message?.includes("statement timeout")
          ) {
            if (!isRetry) {
              await fetchSettlements(true);
              return;
            } else {
              navigate("/laeeblyforshorts");
              return;
            }
          }
          throw error;
        }
        if (data && data.length > 0) {
          const totalCount = data[0].total_count;
          setTotalPages(Math.ceil(totalCount / itemsPerPage));
          setMusicList(data);
          setTotalSum(data[0].total_sum);
          setTotalCommissionSum(data[0].total_sum_commission);
          if (data.length > 0) {
            const uniqueCommissions = [
              ...new Set(data.map((item) => item.commission)),
            ].filter(Boolean);
            setCommission(uniqueCommissions);
          }
        } else {
          setMusicList([]);
          setTotalPages(1);
          setTotalSum(0);
          setTotalCommissionSum(0);
        }
      } catch (err) {
        if (err.message === "timeout") {
          navigate("/laeeblyforshorts");
          return;
        }
        console.error("정산 데이터 조회 실패:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSettlements();
  }, [selectedMonth, currentPage, user?.email]);

  // 월 변경 시 페이지 초기화
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMonth]);

  useEffect(() => {
    const checkContract = async () => {
      if (!user?.email) return setHasContract(false);
      const { data, error } = await supabase
        .from("shorts_contract")
        .select("id, commission")
        .eq("contractor", user.email)
        .maybeSingle();
      if (error) {
        setHasContract(false);
        return;
      }
      setHasContract(!!data);
      if (data) {
        setUserCommission(data.commission || 0);
      }
    };
    checkContract();
  }, [user]);

  const handleSettlementRequest = () => {
    navigate("/report");
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    setIsLoadingModal(true);
    try {
      await SendEmail({
        email: "contact@plpl.kr",
        subject: "계약 상담 신청",
        ccEmails: user?.email ? [user.email] : [],
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "jaeeui.kwon@rhoonart.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "blair.park@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "kirby.lee@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      toast({
        title: "상담 요청이 접수되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setForm({ name: "", email: "", phone: "", channel: "", agree: false });
      onClose();
    } catch (e) {
      toast({
        title: "상담 요청에 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingModal(false);
    }
  };

  useEffect(() => {
    setTempPage(currentPage.toString());
  }, [currentPage]);
  const handlePageInput = (e) => setTempPage(e.target.value);
  const handlePageMove = () => {
    if (/^\d+$/.test(tempPage)) {
      const page = parseInt(tempPage);
      if (page >= 1 && page <= totalPages) setCurrentPage(page);
      else setTempPage(currentPage.toString());
    } else setTempPage(currentPage.toString());
  };

  if (hasContract === null || loading === true) {
    return (
      <Flex minH="60vh" align="center" justify="center" direction="column">
        <Spinner size="xl" color="teal.500" />
      </Flex>
    );
  }
  if (hasContract === false) {
    return (
      <Flex minH="60vh" direction="column" align="center" justify="center">
        <Text fontSize="md" color="gray.700" mb={2}>
          신규 회원은 레이블리와 계약 체결 후 이용이 가능합니다.
        </Text>
        <Button colorScheme="yellow" size="lg" mt={2} onClick={onOpen}>
          계약 상담 신청하기
        </Button>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent maxW="lg">
            <ModalHeader>레이블리 이용 계약 문의하기</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4} fontSize="md">
                신규 회원은 개별 계약 후 이용이 가능합니다. 이용 계약 상담을
                신청해주시면, 기재해주신 연락처로 연락드리겠습니다.
                <br />
                (이메일만 기재할 경우, 스팸으로 분류되는 경우가 많습니다. 이용
                계약 문의주시면, 연락처로 연락드립니다.)
              </Text>
              <FormControl mb={3} isRequired>
                <FormLabel>성함 (필수)</FormLabel>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="성함"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>이메일 (필수)</FormLabel>
                <Input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="이메일"
                  type="email"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>연락처 (필수)</FormLabel>
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="연락처"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>운영중인 유튜브 채널 (필수)</FormLabel>
                <Input
                  name="channel"
                  value={form.channel}
                  onChange={handleChange}
                  placeholder="유튜브 채널"
                />
              </FormControl>
              <Checkbox
                name="agree"
                isChecked={form.agree}
                onChange={handleChange}
                mb={2}
              >
                (필수) 레이블리 이용 상담을 위한 개인정보 제공에 동의합니다.
              </Checkbox>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={isLoadingModal}
                isDisabled={!isFormValid}
                w="100%"
                size="lg"
              >
                상담 요청하기
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh" py={8}>
      <Container maxW="container.xl">
        {/* 헤더 섹션 */}
        <Box mb={8}>
          <HStack spacing={2} mb={2}>
            <Badge
              colorScheme="green"
              fontSize="md"
              px={3}
              py={1}
              borderRadius="full"
            >
              정산 관리
            </Badge>
          </HStack>
          <Text color="gray.600" fontSize="sm">
            레이블리 for 쇼츠에서 발생한 수익을 포함하여, 모든 음원에서 발생한
            수익을 확인할 수 있어요.
          </Text>
        </Box>

        {/* 정산월 선택 및 금액 표시 섹션 */}
        <Flex justify="space-between" align="center" mb={6}>
          <Flex align="center" gap={4}>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              w="150px"
              bg="white"
            >
              {getMonthOptions().map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Box>
              <Text fontWeight="bold" fontSize="sm">
                [정산 월] 정산 금액
              </Text>
              <Text fontWeight="bold" fontSize="sm" color="gray.600">
                {Math.round(totalSum).toLocaleString()}원
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" fontSize="sm">
                수수료
              </Text>
              <Text fontWeight="bold" fontSize="sm" color="gray.500">
                {userCommission}%
              </Text>
            </Box>
          </Flex>
          <Button colorScheme="teal" onClick={handleSettlementRequest}>
            정산 신청
          </Button>
        </Flex>

        {/* 음원 목록 테이블 및 페이지네이션 */}
        <Box bg="white" borderRadius="xl" boxShadow="md" overflow="hidden">
          {loading ? (
            <Flex minH="60vh" align="center" justify="center">
              <Spinner size="xl" color="teal.500" />
            </Flex>
          ) : (
            <>
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>음반</Th>
                    <Th>합계</Th>
                    <Th>유튜브/뮤직/쇼츠</Th>
                    <Th>기타 해외</Th>
                    <Th>멜론</Th>
                    <Th>지니</Th>
                    <Th>바이브</Th>
                    <Th>플로</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {error ? (
                    <Tr>
                      <Td colSpan={8}>
                        <Text color="red.500" textAlign="center" py={4}>
                          {error}
                        </Text>
                      </Td>
                    </Tr>
                  ) : musicList.length === 0 ? (
                    <Tr>
                      <Td colSpan={8}>
                        <Text textAlign="center" py={4} color="gray.500">
                          데이터가 없습니다.
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    musicList.map((music, idx) => (
                      <Tr key={idx}>
                        <Td>
                          <Flex align="center" gap={3}>
                            <Box
                              w="40px"
                              h="40px"
                              borderRadius="md"
                              overflow="hidden"
                              bg="gray.200"
                            >
                              <Image
                                src={music.cover || music.cover_url}
                                alt={music.name_track}
                                w="100%"
                                h="100%"
                                objectFit="cover"
                              />
                            </Box>
                            <Box>
                              <Text fontWeight="medium">
                                {music.name_track}
                              </Text>
                              <Flex align="center" gap={2}>
                                <Text fontSize="sm" color="gray.500">
                                  {music.name_musician}
                                </Text>
                              </Flex>
                            </Box>
                          </Flex>
                        </Td>
                        <Td isNumeric>
                          {Math.round(
                            music.total_settlement || 0
                          ).toLocaleString()}
                          원
                        </Td>
                        <Td isNumeric>
                          {Math.round(
                            music.youtube_amount || 0
                          ).toLocaleString()}
                          원
                        </Td>
                        <Td isNumeric>
                          {Math.round(
                            music.global_amount || 0
                          ).toLocaleString()}
                          원
                        </Td>
                        <Td isNumeric>
                          {Math.round(music.melon_amount || 0).toLocaleString()}
                          원
                        </Td>
                        <Td isNumeric>
                          {Math.round(music.genie_amount || 0).toLocaleString()}
                          원
                        </Td>
                        <Td isNumeric>
                          {Math.round(music.vibe_amount || 0).toLocaleString()}
                          원
                        </Td>
                        <Td isNumeric>
                          {Math.round(music.flo_amount || 0).toLocaleString()}원
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
              {/* 페이지네이션 UI */}
              {totalPages > 1 && (
                <Flex justify="center" mt={4} align="center" mb={4}>
                  <Button
                    onClick={() => setCurrentPage(1)}
                    isDisabled={currentPage === 1}
                    size="sm"
                    mr={1}
                  >
                    처음
                  </Button>
                  <Button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    isDisabled={currentPage === 1}
                    size="sm"
                    mr={2}
                  >
                    이전
                  </Button>
                  <HStack spacing={1} mx={4}>
                    <Input
                      value={tempPage}
                      onChange={handlePageInput}
                      size="sm"
                      width="50px"
                      textAlign="center"
                      fontSize="sm"
                    />
                    <Button
                      size="sm"
                      onClick={handlePageMove}
                      colorScheme="gray"
                      variant="outline"
                      minW="40px"
                      px={2}
                      color="gray.600"
                      borderColor="gray.300"
                      _hover={{ bg: "gray.100", borderColor: "gray.400" }}
                    >
                      이동
                    </Button>
                    <Text>
                      / {totalPages} 페이지 (전체{" "}
                      {musicList[0]?.total_count || 0}개)
                    </Text>
                  </HStack>
                  <Button
                    onClick={() => setCurrentPage((p) => p + 1)}
                    isDisabled={currentPage >= totalPages}
                    size="sm"
                    ml={2}
                  >
                    다음
                  </Button>
                  <Button
                    onClick={() => setCurrentPage(totalPages)}
                    isDisabled={currentPage >= totalPages}
                    size="sm"
                    ml={1}
                  >
                    마지막
                  </Button>
                </Flex>
              )}
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default SettlementManagement;
